import { Delete } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  MenuItem,
  Switch,
  Theme,
  Typography,
  useMediaQuery
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { FC, useEffect, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileDrop, Loader, SMBSelect, Table } from '../../../../components';
import { ExternalLink } from '../../../../components/link/ExternalLink';
import { downloadPandaDoc, getDocumentTypes } from '../../../../fetch';
import { downloadFile, formatDate } from '../../../../helpers';
import { IDocumentType, IVendorCertificate, IVendorDetail } from '../../../../models';
import { MobileCertificate } from '../manage-vendors/MobileCertificate';
import { DatePicker } from '@mui/x-date-pickers';
import { allImageFileTypes } from '../../../../components/file/utils';

interface IUploadFiles {
  pdfFile: File;
  setCertificateWarningChecked: (val: boolean) => void;
  setCertificateStopPayChecked: (val: boolean) => void;
  setSelectedDocType: (val: string) => void;
  selectedDocType: string;
  isCertificateStopPayChecked: boolean;
  isCertificateWarningChecked: boolean;
  setPdfFile: (val: File) => void;
  currentVendor: IVendorDetail;
  setFieldValue: (key: string, val: any) => void;
  setExpirationDate: (val: string | null) => void;
  expirationDate: string | null;
}

export const UploadFiles: FC<IUploadFiles> = ({
  isCertificateWarningChecked,
  isCertificateStopPayChecked,
  pdfFile,
  setPdfFile,
  setFieldValue,
  currentVendor,
  setCertificateWarningChecked,
  setCertificateStopPayChecked,
  setExpirationDate,
  expirationDate,
  setSelectedDocType,
  selectedDocType
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoadingFilterTypes, setLoadingFilterTypes] = useState(false);
  const [selectedDocFilterType, setSelectedDocFilterType] = useState<string>('all');
  const [filterTypes, setFilterTypes] = useState<IDocumentType[]>([]);
  const [isDownLoading, setDownloading] = useState<boolean>(false);
  const isMobile = useMediaQuery('(max-width: 960px)');

  const fetchDocumentTypes = async () => {
    try {
      setLoadingFilterTypes(true);
      const res = await getDocumentTypes({
        perPage: '2147483647' // get all records
      });
      setFilterTypes(res.records);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingFilterTypes(false);
    }
  };

  useEffect(() => {
    fetchDocumentTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVendor]);

  const uploadDZProps = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': []
    },
    onDrop: acceptedFiles => {
      if (acceptedFiles.length > 0) {
        const acceptedFile = acceptedFiles[0];
        setPdfFile(acceptedFile);
        setFieldValue('certificate', acceptedFile.name);
      } else {
        enqueueSnackbar(
          `Invalid file type. Please upload one of these image types, ${allImageFileTypes.join(
            ', '
          )}.`,
          {
            variant: 'error'
          }
        );
      }
    }
  });

  const handlePandaDocDownload = async id => {
    setDownloading(true);
    try {
      const res = await downloadPandaDoc(id);
      const fileHeader = res.headers['content-disposition'];
      // take this ""attachment; filename="Sample Sales Proposal - nhsH9qcdXqejmwipyJhgTP.pdf"; filename*=UTF-8''Sample%20Sales%20Proposal%20-%20nhsH9qcdXqejmwipyJhgTP.pdf""
      // and pull out the correct file name "Sample Sales Proposal - nhsH9qcdXqejmwipyJhgTP.pdf"
      const fileName = fileHeader.split(';')[1].split('filename=')[1].replaceAll('"', '');
      downloadFile(res.data, fileName);
    } catch (error) {
      enqueueSnackbar(`Error, unable to download file, please try again.`, {
        variant: 'error'
      });
    } finally {
      setDownloading(false);
    }
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'File Name',
        accessor: 'fileName',
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IVendorCertificate } };
        }) => {
          // remove characters and replace with spaces
          return original?.fileName && !original?.pandaDocDocumentId ? (
            <ExternalLink type="primary" to={original.url}>
              {original?.fileName?.replaceAll('%20', ' ')}
            </ExternalLink>
          ) : original?.fileName && original?.pandaDocDocumentId ? (
            <Button
              className={classes.pandaFileName}
              color="primary"
              onClick={() => handlePandaDocDownload(original.pandaDocDocumentId)}
            >
              {original?.fileName?.replaceAll('%20', ' ')}
            </Button>
          ) : (
            <span>{original?.fileName}</span>
          );
        }
      },
      selectedDocFilterType === 'all' && {
        Header: 'Type',
        accessor: 'type',
        sort: false
      },
      {
        Header: 'Date Uploaded',
        accessor: 'receivedDate',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IVendorCertificate } };
        }) => {
          return (
            <>
              {!original.receivedDate ? (
                <span className={classes.important}>Pending</span>
              ) : (
                formatDate(original.receivedDate)
              )}
            </>
          );
        }
      },
      {
        Header: 'Expiration Date',
        accessor: 'expirationDate',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IVendorCertificate } };
        }) => {
          return (
            <>
              {!original.expirationDate ? (
                <span className={classes.important}>Pending</span>
              ) : (
                formatDate(original.expirationDate)
              )}
            </>
          );
        }
      },
      {
        Header: 'Warning',
        accessor: 'warning',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IVendorCertificate } };
        }) => {
          return <>{original.warning ? 'Yes' : 'No'}</>;
        }
      },
      {
        Header: 'Stop Pay',
        accessor: 'stopPay',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IVendorCertificate } };
        }) => {
          return <>{original.stopPay ? 'Yes' : 'No'}</>;
        }
      },
      {
        Header: 'User',
        accessor: 'user',
        sort: false
      }
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentVendor, selectedDocFilterType]);

  const filterDocType = filterTypes.find(
    type => type.documentTypeId === Number(selectedDocFilterType)
  );
  const filterTypesNames = filterTypes.map(type => type.name);

  return (
    <>
      {isDownLoading && <Loader type="fullscreen" position="centered" />}
      {currentVendor && (
        <>
          <Grid item xs={6}>
            <Box marginBottom="1rem">
              <SMBSelect
                required
                name="filter-type"
                label="Doc Type Filter"
                disabled={isLoadingFilterTypes}
                id="filter-type"
                value={selectedDocFilterType}
                variant="outlined"
                handleClear={() => setSelectedDocFilterType('')}
                onChange={e => {
                  setSelectedDocFilterType(e.target.value as string);
                }}
              >
                {currentVendor && (
                  <MenuItem key={`All`} value={'all'}>
                    All
                  </MenuItem>
                )}
                {filterTypes &&
                  filterTypes.map((type, index) => {
                    return (
                      <MenuItem key={`${index}`} value={type.documentTypeId}>
                        {type.name}
                      </MenuItem>
                    );
                  })}
                {currentVendor && (
                  <MenuItem key={`Unassigned`} value={'unassigned'}>
                    Unassigned
                  </MenuItem>
                )}
              </SMBSelect>
            </Box>
          </Grid>
          <Table
            columns={columns}
            data={
              filterDocType
                ? // filter by existing types
                  currentVendor.certificates.filter(cert => cert.type === filterDocType.name)
                : // filter by non existing types
                selectedDocFilterType === 'unassigned'
                ? currentVendor.certificates.filter(cert => !filterTypesNames.includes(cert.type))
                : // all filer
                  currentVendor.certificates
            }
            ResponsiveComponent={isMobile ? MobileCertificate : undefined}
          />
        </>
      )}
      {pdfFile && (
        <>
          <Grid item xs={12}>
            <Typography variant="h6">File Upload</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={isCertificateWarningChecked}
                  id="warning"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setCertificateWarningChecked(e.target.checked);
                  }}
                />
              }
              label="Warning"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControlLabel
              control={
                <Switch
                  color="primary"
                  checked={isCertificateStopPayChecked}
                  id="stopPay"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setCertificateStopPayChecked(e.target.checked);
                  }}
                />
              }
              label="Stop Pay"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl
              fullWidth
              required
              variant="outlined"
              size="small"
              error={!selectedDocType ? true : false}
            >
              <SMBSelect
                required
                name="filter-type"
                label="Doc Type"
                id="filter-type"
                variant="outlined"
                handleClear={() => setSelectedDocType('')}
                value={selectedDocType}
                onChange={e => {
                  setSelectedDocType(e.target.value as string);
                }}
              >
                {filterTypes &&
                  filterTypes.map((type, index) => {
                    return (
                      <MenuItem key={`${index}`} value={type.documentTypeId}>
                        {type.name}
                      </MenuItem>
                    );
                  })}
              </SMBSelect>
              {!selectedDocType && <FormHelperText>Required</FormHelperText>}
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl required fullWidth variant="outlined">
              <DatePicker
                label="Expiration Date"
                value={expirationDate}
                onChange={date => {
                  if (date === null) {
                    setExpirationDate(null);
                    return;
                  }
                  setExpirationDate(date);
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    required: true,
                    error: !expirationDate ? true : false,
                    helperText: !expirationDate ? 'Date Required' : ''
                  }
                }}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" alignItems="center">
              <Typography>File selected and ready to save, click save to submit.</Typography>
              <Button
                className={classes.remove}
                onClick={() => {
                  // reset state if they clear the uploaded certificate
                  setPdfFile(null);
                  setFieldValue('certificate', '');
                  setExpirationDate(null);
                  setCertificateStopPayChecked(false);
                  setCertificateWarningChecked(false);
                }}
                startIcon={<Delete className={classes.icon} />}
              >
                Remove File
              </Button>
            </Box>
          </Grid>
        </>
      )}
      {!pdfFile && (
        <Grid item xs={12}>
          <FormControl fullWidth variant="outlined" size="small">
            <FileDrop id="pdf-drop" {...uploadDZProps} title="">
              <Typography variant="body1">
                Drag and drop a file or{' '}
                <Box
                  component="a"
                  href="/#"
                  sx={{ color: theme => theme.palette.primary.main, textDecoration: 'none' }}
                  onClick={e => e.preventDefault()}
                >
                  browse and pick
                </Box>{' '}
              </Typography>
            </FileDrop>
          </FormControl>
        </Grid>
      )}
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  remove: {
    color: theme.palette.error.main,
    fontSize: theme.typography.caption.fontSize,
    margin: 0
  },
  icon: {
    height: '15px',
    marginLeft: theme.spacing(0.5)
  },
  important: {
    color: theme.palette.error.main
  },
  pandaFileName: {
    color: theme.palette.primary.main,
    padding: 0,
    '&:hover': {
      color: theme.palette.primary.dark
    }
  }
}));
